import { useMemo, useState } from "react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, UserPlus } from "lucide-react";
import { useGetDispatchCenters } from "@/api/dispatch-centers";
import {
  type EditableCarrierPortalRole,
  useInviteCarrierPortalUser,
} from "@/api/partner-carriers";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ALL_CENTERS = "__ALL_CENTERS__";

const roleOptions = [
  { value: "CARRIER_OWNER", label: "Carrier Admin" },
  { value: "CARRIER_DISPATCHER", label: "Dispatcher" },
] as const;

function roleLabel(role: EditableCarrierPortalRole) {
  return role === "CARRIER_OWNER" ? "Carrier Admin" : "Dispatcher";
}

const formSchema = z.object({
  email: z.string().email("Enter a valid email"),
  role: z.enum(["CARRIER_OWNER", "CARRIER_DISPATCHER"]),
  dispatchCenterId: z.string(),
});

type FormValues = z.infer<typeof formSchema>;

interface InviteCarrierPortalUserDialogProps {
  open: boolean;
  onClose: () => void;
  carrierId: string;
  carrierName: string;
}

/**
 * Admin-side carrier portal invite dialog. Admins can invite either a Carrier
 * Admin or Dispatcher; the server emails the invitee an accept link.
 */
export function InviteCarrierPortalUserDialog({
  open,
  onClose,
  carrierId,
  carrierName,
}: InviteCarrierPortalUserDialogProps) {
  const [serverError, setServerError] = useState<string | null>(null);
  const dispatchCenters = useGetDispatchCenters();
  const carrierDispatchCenters = useMemo(() => {
    return (
      dispatchCenters.data
        ?.filter((center) => center.pickupProviderCarrier.id === carrierId)
        .map((center) => ({ id: center.id, name: center.name })) ?? []
    );
  }, [carrierId, dispatchCenters.data]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      role: "CARRIER_OWNER",
      dispatchCenterId: ALL_CENTERS,
    },
  });

  const mutation = useInviteCarrierPortalUser({
    onSuccess: (data) => {
      toast.success("Invite sent", {
        description: `${roleLabel(data.invite.role)} invite emailed to ${data.invite.email}.`,
      });
      form.reset();
      setServerError(null);
      onClose();
    },
    onError: (error) => {
      let message = error.message || "Failed to send invite";
      if (axios.isAxiosError(error)) {
        const data = error.response?.data as { error?: string } | undefined;
        if (typeof data?.error === "string") {
          message = data.error;
        }
      }
      setServerError(message);
    },
  });

  const handleSubmit = (values: FormValues) => {
    setServerError(null);
    mutation.mutate({
      carrierId,
      body: {
        email: values.email,
        role: values.role,
        dispatchCenterId:
          values.dispatchCenterId === ALL_CENTERS
            ? null
            : values.dispatchCenterId,
      },
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          form.reset();
          setServerError(null);
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Invite portal user - {carrierName}
          </DialogTitle>
          <DialogDescription>
            Send a carrier portal invite for a Carrier Admin or Dispatcher.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
            noValidate
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="user@partnercarrier.com"
                      autoComplete="off"
                      disabled={mutation.isPending}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={mutation.isPending}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {roleOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="dispatchCenterId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Dispatch center scope</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={mutation.isPending || dispatchCenters.isLoading}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={ALL_CENTERS}>
                        All dispatch centers
                      </SelectItem>
                      {carrierDispatchCenters.map((center) => (
                        <SelectItem key={center.id} value={center.id}>
                          {center.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {serverError && (
              <p className="text-sm text-destructive" role="alert">
                {serverError}
              </p>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending…
                  </>
                ) : (
                  "Send invite"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
